import React from 'react';
import { useGlobalContext } from '../../contexts/GlobalContextProvider';
import { useTheme } from '../../contexts/ThemeContext';
import { FolderOpen, FileText, BookOpen, Database, Check, Sparkles } from 'lucide-react';

/**
 * ActiveContextBadgesApple - Apple UI version with dark mode support
 * Shows active context items as badges on feature pages
 * 
 * Props:
 * - variant: 'compact' | 'full' | 'inline' (default: 'compact')
 * - className: additional CSS classes
 * - showEmpty: show message when no context selected (default: false)
 */
const ActiveContextBadgesApple = ({ variant = 'compact', className = '', showEmpty = false }) => {
  const { isDarkMode } = useTheme();
  
  const { 
    selectedProject, 
    selectedInstruction, 
    selectedGlossary, 
    selectedTM,
    hasContext,
    setShowContextModal 
  } = useGlobalContext();

  if (!hasContext && !showEmpty) {
    return null;
  }

  if (!hasContext && showEmpty) {
    return (
      <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} ${className}`}>
        <span className="italic">No context selected</span>
        <button 
          onClick={() => setShowContextModal(true)}
          className={`${isDarkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'} underline text-xs`}
        >
          Configure
        </button>
      </div>
    );
  }

  const contextItems = [
    { 
      item: selectedProject, 
      icon: FolderOpen, 
      label: 'Project',
      lightBg: 'bg-blue-50', lightText: 'text-blue-700', lightBorder: 'border-blue-200',
      darkBg: 'bg-blue-900/30', darkText: 'text-blue-300', darkBorder: 'border-blue-700',
      name: selectedProject?.name 
    },
    { 
      item: selectedInstruction, 
      icon: FileText, 
      label: 'Instructions',
      lightBg: 'bg-purple-50', lightText: 'text-purple-700', lightBorder: 'border-purple-200',
      darkBg: 'bg-purple-900/30', darkText: 'text-purple-300', darkBorder: 'border-purple-700',
      name: selectedInstruction?.name 
    },
    { 
      item: selectedGlossary, 
      icon: BookOpen, 
      label: 'Glossary',
      lightBg: 'bg-green-50', lightText: 'text-green-700', lightBorder: 'border-green-200',
      darkBg: 'bg-green-900/30', darkText: 'text-green-300', darkBorder: 'border-green-700',
      name: selectedGlossary?.name 
    },
    { 
      item: selectedTM, 
      icon: Database, 
      label: 'TM',
      lightBg: 'bg-amber-50', lightText: 'text-amber-700', lightBorder: 'border-amber-200',
      darkBg: 'bg-amber-900/30', darkText: 'text-amber-300', darkBorder: 'border-amber-700',
      name: selectedTM?.name 
    },
  ].filter(ctx => ctx.item);

  if (variant === 'inline') {
    // Single line version for tight spaces
    return (
      <div className={`flex items-center gap-1.5 ${className}`}>
        <Sparkles size={12} className={isDarkMode ? 'text-purple-400' : 'text-purple-500'} />
        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Using:</span>
        {contextItems.map((ctx, idx) => (
          <span 
            key={idx}
            className={`text-xs font-medium ${isDarkMode ? ctx.darkText : ctx.lightText}`}
          >
            {ctx.name}{idx < contextItems.length - 1 ? ',' : ''}
          </span>
        ))}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
        {contextItems.map((ctx, idx) => (
          <div 
            key={idx}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
              ${isDarkMode ? `${ctx.darkBg} ${ctx.darkText}` : `${ctx.lightBg} ${ctx.lightText}`}
            `}
            title={`${ctx.label}: ${ctx.name}`}
          >
            <Check size={10} className="flex-shrink-0" />
            <span className="truncate max-w-[100px]">{ctx.name}</span>
          </div>
        ))}
      </div>
    );
  }

  // Full variant with labels
  return (
    <div className={`rounded-xl p-3 ${
      isDarkMode 
        ? 'bg-gray-800/50 border border-gray-700' 
        : 'bg-gray-50 border border-gray-200'
    } ${className}`}>
      <div className={`flex items-center gap-1.5 text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <Check size={12} className="text-green-500" />
        <span className="font-medium">Active Context</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {contextItems.map((ctx, idx) => {
          const Icon = ctx.icon;
          return (
            <div 
              key={idx}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border
                ${isDarkMode 
                  ? `${ctx.darkBg} ${ctx.darkText} ${ctx.darkBorder}` 
                  : `${ctx.lightBg} ${ctx.lightText} ${ctx.lightBorder}`
                }
              `}
            >
              <Icon size={12} className="flex-shrink-0" />
              <span className="truncate max-w-[120px]">{ctx.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActiveContextBadgesApple;
